import React, { useRef, useState } from "react";
import imageService from "../../services/image";
import { useMutation } from "@apollo/client";
import RichTextEditor from "react-rte";
import imageCompression from "browser-image-compression";
import {
  Box,
  TextField,
  Typography,
  Alert,
  FormHelperText,
  Button,
  CircularProgress,
  Card,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  BLOGS,
  CREATE_BLOG,
  SEND_BLOG_NOTIFICATION,
} from "../../gql/blog";
import { useNavigate } from "react-router-dom";

const fileTypes = [
  "image/apng", "image/bmp", "image/gif", "image/jpeg", "image/pjpeg",
  "image/png", "image/svg+xml", "image/tiff", "image/webp", "image/x-icon",
];

const toolbarConfig = {
  display: ["INLINE_STYLE_BUTTONS", "BLOCK_TYPE_BUTTONS", "LINK_BUTTONS", "BLOCK_TYPE_DROPDOWN", "HISTORY_BUTTONS"],
  INLINE_STYLE_BUTTONS: [
    { label: "Bold", style: "BOLD", className: "custom-css-class" },
    { label: "Italic", style: "ITALIC" },
    { label: "Underline", style: "UNDERLINE" },
  ],
  BLOCK_TYPE_DROPDOWN: [
    { label: "Normal", style: "unstyled" },
    { label: "Heading Large", style: "header-one" },
    { label: "Heading Medium", style: "header-two" },
    { label: "Heading Small", style: "header-three" },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: "UL", style: "unordered-list-item" },
    { label: "OL", style: "ordered-list-item" },
  ],
};

const sectionCard = {
  borderRadius: "12px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  p: 3,
  mb: 2.5,
};

const CreateBlog = ({ onCreated }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const [values, setValues] = useState({ title: "", body: "", title_image_url: "" });
  const [errors, setErrors] = useState({ title: "", body: "", title_image_url: "" });
  const [imagePreview, setImagePreview] = useState(null);
  const [image, setImage] = useState();
  const [textValue, setTextValue] = useState(RichTextEditor.createEmptyValue());
  const [textValueMM, setTextValueMM] = useState(RichTextEditor.createEmptyValue());
  const [sendPush, setSendPush] = useState(true);
  const [schedulePush, setSchedulePush] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const fileInputRef = useRef(null);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const [sendBlogNotification] = useMutation(SEND_BLOG_NOTIFICATION);

  const [createBlog] = useMutation(CREATE_BLOG, {
    refetchQueries: [BLOGS],
    onError: (error) => {
      console.log("error : ", error);
      alert("Blog Upload Error");
      setShowAlert({ message: "Error on server", isError: true });
      setTimeout(() => setShowAlert({ message: "", isError: false }), 3000);
      setLoading(false);
    },
  });

  const imageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      let img = e.target.files[0];
      if (!fileTypes.includes(img.type)) {
        setErrors({ ...errors, title_image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)" });
        return;
      }
      setImagePreview(URL.createObjectURL(img));
      setImage(img);
      setErrors({ ...errors, title_image_url: "" });
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setErrors({ title: "", paragraph: "", image_url: "" });
    let isErrorExit = false;
    let errorObject = {};
    if (!values.title) { errorObject.title = "Title field is required."; isErrorExit = true; }
    if (!values.body) { errorObject.body = "Body field is required."; isErrorExit = true; }
    if (!values.body_mm) { errorObject.body_mm = "Body MM field is required."; isErrorExit = true; }
    if (sendPush && schedulePush) {
      if (!scheduledAt) {
        errorObject.scheduledAt = "Schedule time is required.";
        isErrorExit = true;
      } else {
        const parsedScheduledAt = new Date(scheduledAt);
        if (Number.isNaN(parsedScheduledAt.getTime()) || parsedScheduledAt.getTime() <= Date.now()) {
          errorObject.scheduledAt = "Schedule time must be in the future.";
          isErrorExit = true;
        }
      }
    }
    if (isErrorExit) { setErrors({ ...errorObject }); setLoading(false); return; }
    try {
      const options = { initialQuality: 0.5, maxWidthOrHeight: 720, useWebWorker: true };
      const compressedTitleImage = await imageCompression(image, options);
      const titleResult = await imageService.uploadFile(compressedTitleImage);
      const thumbOptions = { initialQuality: 0.5, maxWidthOrHeight: 360, useWebWorker: true };
      const compressedThumbImage = await imageCompression(image, thumbOptions);
      const thumbResult = await imageService.uploadFile(compressedThumbImage);
      const { data } = await createBlog({
        variables: { ...values, thumbnail_image_url: thumbResult.imageUploadUrl, title_image_url: titleResult.imageUploadUrl },
      });
      const blog = data?.insert_blog_one;
      let notificationFailed = false;
      let notificationScheduled = false;
      if (blog?.id && sendPush) {
        try {
          const { data: notifyData } = await sendBlogNotification({
            variables: {
              blog_id: blog.id,
              title: blog.title ?? values.title,
              scheduled_at:
                schedulePush && scheduledAt
                  ? new Date(scheduledAt).toISOString()
                  : null,
            },
          });
          if (notifyData?.sendBlogNotification?.error) {
            notificationFailed = true;
          } else if (schedulePush) {
            notificationScheduled = true;
          }
        } catch (notifyError) { notificationFailed = true; }
      }
      setValues({ title: "", body: "", title_image_url: "" });
      setErrors({ title: "", body: "", title_image_url: "" });
      setTextValue(RichTextEditor.createEmptyValue());
      setTextValueMM(RichTextEditor.createEmptyValue());
      setImagePreview(null);
      setImage(undefined);
      setSendPush(true);
      setSchedulePush(false);
      setScheduledAt("");
      setLoading(false);
      onCreated?.();
      navigate("/blog", {
        state: {
          successMessage: "Blog have been created.",
          warningMessage: notificationFailed
            ? "Blog created, but notification failed to send."
            : notificationScheduled
            ? "Blog created. Push notification was scheduled."
            : "",
        },
      });
    } catch (error) {
      console.log("error : ", error);
      setLoading(false);
    }
  };

  const onChange = (value) => {
    setTextValue(value);
    setValues({ ...values, body: value.toString("html") });
  };
  const onChangeMM = (value) => {
    setTextValueMM(value);
    setValues({ ...values, body_mm: value.toString("html") });
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eee",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Create Blog
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Write and publish a new blog post
          </Typography>
        </Box>
        <Button onClick={() => navigate("/blog")} variant="outlined" color="inherit" size="small" sx={{ borderColor: "#ddd", color: "#555" }}>
          Close
        </Button>
      </Box>

      <Box sx={{ maxWidth: 960, mx: "auto", px: 3, py: 3 }}>
        {/* Cover Image Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Cover Image
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: 240,
                height: 160,
                border: errors.title_image_url ? "2px dashed #d32f2f" : "2px dashed #ddd",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                bgcolor: "#fafafa",
                overflow: "hidden",
                transition: "all 0.2s",
                flexShrink: 0,
                "&:hover": { borderColor: "#ff5722", bgcolor: "rgba(255,87,34,0.03)" },
              }}
            >
              {imagePreview ? (
                <Box component="img" src={imagePreview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 36, color: "#bbb", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" textAlign="center" px={1}>
                    Click to upload
                  </Typography>
                  <Typography variant="caption" color="text.disabled">PNG, JPG, GIF</Typography>
                </>
              )}
            </Box>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={imageChange} style={{ display: "none" }} />
            <Box>
              {imagePreview ? (
                <Chip label="Image selected ✓" color="success" size="small" sx={{ mb: 1 }} />
              ) : (
                <Typography variant="body2" color="text.secondary" mb={1}>Upload a cover image for this blog post</Typography>
              )}
              {errors.title_image_url && <Typography variant="caption" color="error">{errors.title_image_url}</Typography>}
            </Box>
          </Box>
        </Card>

        {/* Blog Info Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Blog Info
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <TextField
            id="title"
            label="Title *"
            fullWidth
            value={values.title || ""}
            onChange={handleChange("title")}
            error={!!errors.title}
            helperText={errors.title}
          />
        </Card>

        {/* Content Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Content
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                English *
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChange}
                value={textValue || ""}
                toolbarConfig={toolbarConfig}
              />
              {errors.body && <FormHelperText error>{errors.body}</FormHelperText>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Myanmar *
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChangeMM}
                value={textValueMM}
                toolbarConfig={toolbarConfig}
              />
              {errors.body_mm && <FormHelperText error>{errors.body_mm}</FormHelperText>}
            </Box>
          </Box>
        </Card>

        {/* Push Notification Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Push Notification
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendPush}
                onChange={(e) => {
                  setSendPush(e.target.checked);
                  if (!e.target.checked) {
                    setSchedulePush(false);
                    setScheduledAt("");
                  }
                }}
              />
            }
            label="Send push notification"
          />
          {sendPush && (
            <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={schedulePush}
                    onChange={(e) => {
                      setSchedulePush(e.target.checked);
                      if (!e.target.checked) {
                        setScheduledAt("");
                        setErrors((prev) => ({ ...prev, scheduledAt: "" }));
                      }
                    }}
                  />
                }
                label="Schedule for later"
              />
              {schedulePush && (
                <TextField
                  label="Schedule At"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => {
                    setScheduledAt(e.target.value);
                    if (errors.scheduledAt) {
                      setErrors((prev) => ({ ...prev, scheduledAt: "" }));
                    }
                  }}
                  error={!!errors.scheduledAt}
                  helperText={
                    errors.scheduledAt ||
                    "Push will send automatically at this time (local)."
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ maxWidth: 360 }}
                />
              )}
            </Box>
          )}
        </Card>

        {/* Footer Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1, pb: 4 }}>
          <Button onClick={() => navigate("/blog")} variant="outlined" color="inherit" size="large" sx={{ borderColor: "#ddd", color: "#555", minWidth: 100 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={handleCreate}
            sx={{
              minWidth: 130,
              bgcolor: "#ff5722",
              "&:hover": { bgcolor: "#e64a19" },
              "&.Mui-disabled": { bgcolor: "#ffccbc", color: "#fff" },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
            {loading ? "Publishing..." : "Publish Blog"}
          </Button>
        </Box>
      </Box>

      {showAlert.message && !showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="success">{showAlert.message}</Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="error">{showAlert.message}</Alert>
      )}
    </Box>
  );
};

export default CreateBlog;
